import React, { useEffect, useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import ApiSettings from './ApiSettings';
import SyncManager from './SyncManager';
import PrinterSettings from './PrinterSettings';
import { Shield, Webhook, Database, RefreshCw, Printer } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { useIsMobile } from '@/hooks/use-mobile';

const SettingsPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('api');
  const isMobile = useIsMobile();
  
  useEffect(() => {
    // Verificar se existe um parâmetro 'tab' na URL
    const tabParam = searchParams.get('tab');
    if (tabParam && ['api', 'data', 'storage', 'printer'].includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, [searchParams]);
  
  return (
    <div className="container py-6">
      <h1 className="text-2xl font-bold mb-6">Configurações Administrativas</h1>
      
      <div className="bg-blue-50 p-6 rounded-lg border-2 border-blue-300 shadow-md mb-6">
        <div className="flex items-center">
          <Shield className="h-8 w-8 text-blue-600 mr-4" />
          <div>
            <h2 className="text-xl font-bold text-blue-800">Área de Administração</h2>
            <p className="text-blue-600">Configure a integração com o ERP, sincronização de dados e mais opções administrativas</p>
          </div>
        </div>
      </div>
      
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="api" className="flex items-center justify-center gap-2">
            <Webhook className="h-4 w-4 flex-shrink-0" />
            {!isMobile && <span>API do ERP</span>}
          </TabsTrigger>
          <TabsTrigger value="data" className="flex items-center justify-center gap-2">
            <RefreshCw className="h-4 w-4 flex-shrink-0" />
            {!isMobile && <span>Sincronização</span>}
          </TabsTrigger>
          <TabsTrigger value="storage" className="flex items-center justify-center gap-2">
            <Database className="h-4 w-4 flex-shrink-0" />
            {!isMobile && <span>Armazenamento</span>}
          </TabsTrigger>
          <TabsTrigger value="printer" className="flex items-center justify-center gap-2">
            <Printer className="h-4 w-4 flex-shrink-0" />
            {!isMobile && <span>Impressão</span>}
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="api" className="py-4">
          <ApiSettings />
        </TabsContent>
        
        <TabsContent value="data" className="py-4">
          <SyncManager />
        </TabsContent>
        
        <TabsContent value="storage" className="py-4">
          <div className="bg-muted p-4 rounded-md">
            <h3 className="text-lg font-medium mb-2">Armazenamento de Dados</h3>
            <p className="text-sm text-muted-foreground">
              O aplicativo utiliza IndexedDB para armazenar dados localmente, permitindo que o sistema funcione offline. 
              Os dados sincronizados com o ERP são armazenados localmente e podem ser acessados mesmo sem conexão com a internet.
            </p>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-card p-3 rounded border">
                <h4 className="font-medium">Dados armazenados:</h4>
                <ul className="mt-2 space-y-1 text-sm">
                  <li>• Produtos e categorias</li>
                  <li>• Pedidos e itens</li>
                  <li>• Configurações de usuário</li>
                  <li>• Configurações da API</li>
                </ul>
              </div>
              <div className="bg-card p-3 rounded border">
                <h4 className="font-medium">Benefícios:</h4>
                <ul className="mt-2 space-y-1 text-sm">
                  <li>• Funciona offline</li>
                  <li>• Maior velocidade no acesso</li>
                  <li>• Sincronização automática</li>
                  <li>• Armazenamento seguro</li>
                </ul>
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="printer" className="py-4">
          <PrinterSettings />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SettingsPage;
